import {
  EditOutlined,
  MenuOutlined,
  PushpinFilled,
  PushpinOutlined,
  SolutionOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import { RocketOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { Button, Dropdown, Menu } from 'antd'
import { Tooltip } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  GetViewerFollowingGroupsDocument,
  Group,
  GroupPrivateSetting,
  useGetViewerQuery,
  useGroupPinUpdateMutation,
} from '@/generated/graphql'
import { useIsSystemAdmin } from '@/modules/admin/admin.hooks'
import { Follow } from '@/modules/follow'
import { ArchiveGroup } from '@/modules/group/archive-group'
import { GroupCreateEdit } from '@/modules/group/group-create-edit'
import { useFeatureGroup, useIsGroupAdmin } from '@/modules/group/group.hooks'
import { InviteUser } from '@/modules/group/invite-user'

import { GroupApplyModal } from '../group-apply/group-apply.component'
import s from './group-menu.module.less'

interface GroupMenuProps {
  group: Group
}

export const GroupMenu: React.FC<GroupMenuProps> = ({ group }) => {
  const [t] = useTranslation()
  const [dropDownVisible, setDropDownVisible] = React.useState(false)

  const { data: me } = useGetViewerQuery()

  const [groupPinUpdate] = useGroupPinUpdateMutation({
    refetchQueries: [getOperationName(GetViewerFollowingGroupsDocument)!],
  })

  const { isGroupAdmin, isPrivateGroupAdmin } = useIsGroupAdmin(group)
  const [featureGroup, unFeatureGroup] = useFeatureGroup(group.groupId)

  const isPrivate = group.privateSetting === GroupPrivateSetting.Closed
  const isSystemAdmin = useIsSystemAdmin()

  const SysAdminNotAllowed = isPrivate && isSystemAdmin && !group.isFollowing
  const showActionMenu = isGroupAdmin || group.isFollowing
  const isCreator = me?.viewer.userId === group.creatorUserId

  const editMenuItem = isGroupAdmin && (
    <Menu.Item>
      <GroupCreateEdit group={group}>
        <div className={s.menu}>
          <EditOutlined />
          {t('group.edit')}
        </div>
      </GroupCreateEdit>
    </Menu.Item>
  )

  const archiveMenuItem = me!.viewer.userId === group.creatorUserId && (
    <Menu.Item>
      <ArchiveGroup setDropDownVisible={setDropDownVisible} group={group} />
    </Menu.Item>
  )

  const inviteUserMenuItem = !SysAdminNotAllowed && isGroupAdmin && (
    <Menu.Item>
      <InviteUser group={group}>
        <div className={s.menu}>
          <UserAddOutlined />
          {t('group.addUser')}
        </div>
      </InviteUser>
    </Menu.Item>
  )

  const cancelQuickAccessMenuItem = group.isFollowing && group.isPinned && (
    <Menu.Item
      className={s.menu}
      onClick={() => {
        groupPinUpdate({ variables: { groupId: group.groupId } })
      }}
    >
      {group.isPinned ? <PushpinFilled /> : <PushpinOutlined />}
      {t(`group.${group.isPinned ? 'cancelQuickAccess' : 'quickAccess'}`)}
    </Menu.Item>
  )

  const unfollowMenuItem = group.isFollowing && group.canUnfollow && !isCreator && (
    <Menu.Item className={s.menu}>
      <Follow
        component={<div />}
        display={'both'}
        followeeGroupId={group.groupId}
        isFollowing={group.isFollowing}
        isPrivate={isPrivate}
        isPinned={group.isPinned}
      />
    </Menu.Item>
  )

  const featureMenuItem = isSystemAdmin && (
    <>
      <Menu.Divider />
      <Menu.Item icon={<RocketOutlined />} onClick={() => (group.featuredAt ? unFeatureGroup() : featureGroup())}>
        <Tooltip title={group.featuredAt ? t('group.unFeatureTooltip') : t('group.featuredTooltip')}>
          {group.featuredAt ? t('group.unFeature') : t('group.feature')}
        </Tooltip>
      </Menu.Item>
    </>
  )

  const groupAction = (
    <Menu onMouseLeave={() => setDropDownVisible(false)}>
      {editMenuItem}
      {archiveMenuItem}
      {inviteUserMenuItem}
      {unfollowMenuItem}
      {cancelQuickAccessMenuItem}
      {featureMenuItem}
    </Menu>
  )

  const groupActionMenu = (
    <Dropdown {...(dropDownVisible && { visible: dropDownVisible })} trigger={['click']} overlay={groupAction}>
      <Button icon={<MenuOutlined />}>
        <span className={s.buttonText}>{t('group.more')}</span>
      </Button>
    </Dropdown>
  )

  const quickAccessButton = group.isFollowing && !group.isPinned && (
    <Button
      onClick={() => {
        groupPinUpdate({ variables: { groupId: group.groupId } })
      }}
      icon={group.isPinned ? <PushpinFilled /> : <PushpinOutlined />}
    >
      <span className={s.buttonText}>{t(`group.${group.isPinned ? 'cancelQuickAccess' : 'quickAccess'}`)}</span>
    </Button>
  )

  const joinButton = !group.isFollowing && !isPrivate && (
    <Follow display={'both'} followeeGroupId={group.groupId} isFollowing={group.isFollowing} isPrivate={isPrivate} />
  )

  const applyListButton = isPrivate && isPrivateGroupAdmin && (
    <GroupApplyModal groupId={group.groupId}>
      <Button icon={<SolutionOutlined />}>
        <span className={s.buttonText}>{t('group.applyList.applyList')}</span>
      </Button>
    </GroupApplyModal>
  )

  return (
    <div className={s.actions}>
      {applyListButton}
      {joinButton}
      {quickAccessButton}
      {showActionMenu && !SysAdminNotAllowed && groupActionMenu}
    </div>
  )
}
